import { DPD_LOGO } from './constants'

export default {
  loading: false,
  version: "0.0.0",
  dialog: null,

  wtRegion: "US",
  isoCode: "en-US",

  toolsCenter: false,
  importantInfoAccepted: false,
  dpdIsOpen: false,

  features: {
    "clientPDF": true,
    "tradeTicket": true,
    "stressTests": true,
    "customStressTests": true,
    "hypotheticalFutureGrowth": true,
    "benchmarks": true,
    "fundamentals": true,
  },

  // Entry
  entry: {
    compareMode: false,
    inFocus: "original",
    benchmarks: [],
    selectedBenchmark: null,
    modelPortfolios: [], 
    uniqueModelFamilies: [], 
    personalStrategies: [],
    original: {
      id: null,
      portfolio: [],
      weightings: {},
      name: null,    
      preparedForEmail: null,
      investmentGoal: 'P',
      bestModel: false,
      filterFundYears: false,
      filterFundSize: false,
      entityCCY: 'USD',
      modelProposalTicker: null,
      selectedModel: null,
      selectedModelFamily: 'null',
      selectedPersonalStrategy: 'null',
      showModelInput: 'fund',
      usingModel: false,
    },
    proposed: {},
  },

  pendingSaveToMyPortfolios: {   
    original: {
      portfolioId: null,
      complete: null,
      personalStrategyId: null,
    },
    proposed: {
      portfolioId: null,
      complete: null,
      personalStrategyId: null,
    }
  },

  preexistingWeightings: {
    original: null,
    proposed: null,
  },
  percentageWeightings: {
    original: {},
    proposed: {},
  },
  loadingFundSuggestions: {
    original: false,
    proposed: false,
  },
  fixedWeighting: {
    original: false,
    proposed: false,
  },
  fundSuggestions: [],

  pendingDeletePortfolio: {},
  pendingFavoritePortfolio: {},

  investmentGoalOptions: [
    // label now loaded from complianceText, altLabel from Dave
    { value: 'P' }, // Performance
    { value: 'I' }, // Income
    { value: 'R' }, // Returns
    { value: 'S' }, // Sharpe Ratio
    { value: 'F' }, // Fees
    { value: 'C', label: 'Comparison' }, // Comparison
  ],
  bestModelOptions: [
    { value: false },
    { value: true, label: 'Show me your best WisdomTree model portfolio idea' },
  ],
  wtRegionCCYOptions: {
    'US': [
      { value: 'USD' }
    ],
    'CA': [
      { value: 'CAD' },
      { value: 'USD' },
    ],
    'GB': [
      { value: 'GBP' },
    ],
    'IT': [
      { value: 'EUR' },
    ],
    'DE': [ 
      { value: 'EUR' },
    ],
    'BE': [ 
      { value: 'EUR' },
    ],
    'DK': [ 
      { value: 'EUR' },
    ],
    'ES': [ 
      { value: 'EUR' },
    ],
    'FI': [ 
      { value: 'EUR' },
    ],
    'FR': [ 
      { value: 'EUR' },
    ],
    'IE': [ 
      { value: 'EUR' },
    ],
    'LU': [ 
      { value: 'EUR' },
    ],
    'NO': [ 
      { value: 'EUR' },
    ],
    'SE': [ 
      { value: 'EUR' },
    ],
    'CH': [ 
      { value: 'EUR' },
    ],
    'AT': [ 
      { value: 'EUR' },
    ],
    'NL': [ 
      { value: 'EUR' },
    ],
  },

  // Report

  analysis: null,

  graphic: {
    layout: 'Bar chart',
    aggregateBy: 'country',
  },

  performanceSplit: false,
  incomeSplit: false,
  compositionSplit: false,
  monteCarloSplit: true,
  pdfStatus: 'unsent',
  ticketStatus: 'unsent',
  awaitingNewData: false,
  salesforceComplete: false,

  clientPdf: {
    status: "unsent",
    clientName: null,
    logoKey: 'defaultLogo',
    allowClientLogo: null,
    clientLogo: null,
    savedLogo: null,
    defaultLogo: DPD_LOGO,
    description: null,
    addlFees: 0,
    sections: {},
    stressTests: {},
  },

  gutter: 50, // sync with variables.scss $gutter-size

  keyMetrics: [
    {
      title: 'Performance',
      key: 'P',
      value: 'avgAnnualPerformance',
      multiplier: 100,
      precision: 1,
      bounds: [0.85, 1.15],
    },
    {
      title: 'Income',
      key: 'I',
      value: 'dividendYield',
      multiplier: 100,
      precision: 1,
      bounds: [-0.2, 0.2],
    },
    {
      title: 'Risk',
      key: 'R',
      value: 'avgAnnualVolatility',
      multiplier: 100,
      precision: 1,
      bounds: [0.2, 0],
    },
    {
      title: 'Risk-adj. Returns',
      key: 'S',
      value: 'sharpeRatio',
      multiplier: 1,
      precision: 2,
      bounds: [0, 0.01],
    },
    {
      title: 'Fees',
      key: 'F',
      value: 'aggregateExpenseRatio',
      multiplier: 1e4,
      precision: 0,
      bounds: [0.01, 0],
    },
  ],

  exposureOptions: [
    { value: 'country', label: 'Geography' },
    { value: 'marketCap', label: 'Market Cap' },
    { value: 'assetClass', label: 'Asset Class' },
    { value: 'sector', label: 'Sector' },
    { value: 'creditQuality', label: 'Credit Quality' },
    { value: 'maturity', label: 'Bond Maturity' },
    // { value: 'currency', label: 'Fixed Income Currency' },
  ],

  compositionFields: [
    {
      key: 'domicile',
      name: 'Domicile',
      group: 'Comp',
      wtRegion: 'CA',
    },
    {
      key: 'assetClass',
      name: 'Asset Class',
      group: 'Comp' 
    },
    { 
      key: 'geoFocus', 
      name: 'Geo Focus', 
      group: 'Comp' 
    },
    {
      key: 'weighting',
      name: 'Weighting (%)',
      type: 'numeric-column',
      multiplier: 100,
      precision: 1,
      aggregation: true,
      group: 'Comp'
    },
    {
      key: 'expenseRatio',
      name: 'Expense (bps)',
      type: 'numeric-column',
      multiplier: 1e4,
      precision: 0,
      aggregation: 'weighting',
      group: 'Comp'
    },
  ],

  famaFrench5FactorFields: [
    { key: 'fff-beta' },
    { key: 'fff-size' },
    { key: 'fff-value' },
    { key: 'fff-quality' },
    { key: 'fff-investments' },
  ],

  performanceTableFields: [
    { key: 'mtd', prefix: '1-', group: 'Cumulative' },
    { key: 'mos3', prefix: '3-', group: 'Cumulative' },
    { key: 'ytd', prefix: 'YTD', group: 'Cumulative' },
    { key: 'yrs1', prefix: '1-', group: 'Average' },
    { key: 'yrs3', prefix: '3-', group: 'Average' },
    { key: 'yrs5', prefix: '5-', group: 'Average' },
    { key: 'yrs7', prefix: '7-', group: 'Average' },
    { key: 'yrs10', prefix: '10-', group: 'Average' },
    { key: 'inception', group: 'Average' },
    { key: 'commonPeriod', group: 'CP' },
  ],

  fundDetailsFields: [
    {
      key: 'fundPageURL',
      type: 'fd-link',
    },
    {
      key: 'morningstar',
      type: 'fd-link',
      wtRegion: 'US',
    },
    { 
      key: 'inceptionDate', 
      type: 'fd-narrow',
    },
    { 
      key: 'expenseRatioGross', 
      name: 'Gross Expense Ratio', 
      type: 'fd-narrow',
      multiplier: 1e4,
      precision: 0,
      wtRegion: 'US',
    },
    { 
      key: 'expenseRatio', 
      type: 'fd-narrow',
      multiplier: 1e4,
      precision: 0,
    },
    {
      key: 'sec30DayYieldSub',
      type: 'fd-narrow',
      precision: 2,
      asOfDate: true,
      wtRegion: 'US',
    },
    {
      key: 'sec30DayYieldUnSub',
      type: 'fd-narrow',
      precision: 2,
      asOfDate: true,
      wtRegion: 'US',
    },
    { 
      key: 'expenseNote', 
      type: 'fd-wide',
    },
  ],

  stressTestKeys: {
    "bullMarket": {},
    "bearMarket": {},
    "crash2008": {},
    "techBubble2000": {},
    "ltcmRuble1998": {},
    "fedHike1994": {},
  },

  customStressTests: [
    {
      name: "New scenario",
      permanent: true,
      factorShifts: {
        "betaEquityUS": 0,
        "betaEquityEuroDev": 0,
        "betaEquityAsiaDev": 0,
        "betaEquityChina": 0,
        "betaEquityEm": 0,
        "betaHml": 0,
        "betaSmb": 0,
        "betaRates3moUS": 0,
        "betaRates1yrUS": 0,
        "betaRates5yrUS": 0,
        "betaRates10yrUS": 0,
        "betaRates30yrUS": 0,
        "betaCreditSpreadBBBUS": 0,
        "betaUSDTradeweighted": 0,
        "betaGold": 0,
        "betaOil": 0
      }
    },
    {
      name: "Examples",
      permanent: true,
      flagFake: true,
      factorShifts: {
        "betaEquityUS": 0,
        "betaEquityEuroDev": 0,
        "betaEquityAsiaDev": 0,
        "betaEquityChina": 0,
        "betaEquityEm": 0,
        "betaHml": 0,
        "betaSmb": 0,
        "betaRates3moUS": 0,
        "betaRates1yrUS": 0,
        "betaRates5yrUS": 0,
        "betaRates10yrUS": 0,
        "betaRates30yrUS": 0,
        "betaCreditSpreadBBBUS": 0,
        "betaUSDTradeweighted": 0,
        "betaGold": 0,
        "betaOil": 0
      }
    }
  ],
  customStressTestIsOpen: false,
  basicCustomStressTests: [],
  selectedCustomStressTest: 0,
  customStressTestIsBasic: true,
  customStressTestColumns: {
    equity: {
      "betaEquityUS": "US equity market",
      "betaEquityEuroDev": "European equity market",
      "betaEquityAsiaDev": "Japanese equity market",
      "betaEquityChina": "Chinese equity market",
      "betaEquityEm": "Emerging Markets equities",
    },
    rates: {
      "betaRates3moUS": "90 Day T-Bill",
      "betaRates1yrUS": "1 year",
      "betaRates5yrUS": "5 year",
      "betaRates10yrUS": "10 year",
      "betaRates30yrUS": "30 year",
      "betaCreditSpreadBBBUS": "US Corp. Bond Yield Spread",
    },
    other: {
      "betaSmb": "Size (SMB)",
      "betaHml": "Value (HML)",
      "betaUSDTradeweighted": "US Dollar Index",
      "betaGold": "Gold Price",
      "betaOil": "Oil Prices",
    }
  },
  riskFreeRates: {
    "90 Day T-Bill": 0,
    "1 year": 0,
    "5 year": 0,
    "10 year": 0,
    "30 year": 0,
    "US Corp. Bond Yield Spread": 0,
  },

  monteCarlos: [
    { 
      name: "New scenario",
      permanent: true,
      expectedReturnAssumptions: {
        "betaEquityUS": 0,
        "betaEquityEuroDev": 0,
        "betaEquityAsiaDev": 0,
        "betaEquityChina": 0,
        "betaEquityEm": 0,
        "betaHml": 0,
        "betaSmb": 0,
        "betaRates3moUS": 0,
        "betaRates1yrUS": 0,
        "betaRates5yrUS": 0,
        "betaRates10yrUS": 0,
        "betaRates30yrUS": 0,
        "betaCreditSpreadBBBUS": 0,
        "betaUSDTradeweighted": 0,
        "betaGold": 0,
        "betaOil": 0
      },
      expectedVolatilityAssumptions: {
        "betaEquityUS": 0,
        "betaEquityEuroDev": 0,
        "betaEquityAsiaDev": 0,
        "betaEquityChina": 0,
        "betaEquityEm": 0,
        "betaHml": 0,
        "betaSmb": 0,
        "betaRates3moUS": 0,
        "betaRates1yrUS": 0,
        "betaRates5yrUS": 0,
        "betaRates10yrUS": 0,
        "betaRates30yrUS": 0,
        "betaCreditSpreadBBBUS": 0,
        "betaUSDTradeweighted": 0,
        "betaGold": 0,
        "betaOil": 0
      },
    },
  ],
  monteCarloIsOpen: false,
  selectedMonteCarlo: 0,

  defaultPortfolio: {
    "name": "Example Portfolio",
    "investmentGoal": "P",
    "bestModel": false,
    "holdings": [
      {
        "names": [
          "WisdomTree U.S. MidCap Dividend Fund",
          "iShares Russell 3000 ETF",
          "First Trust Large Cap Value AlphaDEX Fund",
          "iShares MSCI EAFE ETF",
          "iShares MSCI Emerging Markets ETF",
          "iShares Core US Aggregate Bond ETF"
        ],
        "types": [
          "WTETF",
          "ETF",
          "ETF",
          "ETF",
          "ETF",
          "ETF",
        ],
        "tickers": [
          "DON",
          "IWV",
          "FTA",
          "EFA",
          "EEM",
          "AGG"
        ],
        "weights": [
          118000,
          176000,
          176000,
          176000,
          119000,
          235000
        ]
      }
    ]
  }

};



// WEBPACK FOOTER //
// ./src/initialState.js